/**
 * buildSystemEventContext — canonical shape for system_events.context JSONB.
 *
 * Every call site should use this helper so Admin Debug View can rely on
 * consistent keys. Missing scalars default to null; extra defaults to {}.
 *
 * @see docs/release/observability-audit-2026-03-08.md → "Canonical context payload"
 */

import type { ReasonKind } from './logSystemEvent';

export interface SystemEventContext {
  [key: string]: unknown;
  /** Feature area: publish_producer, activity_editor, media_upload, booking, shop, admin_studio */
  feature: string | null;
  /** Verb: save, publish, unpublish, cancel, upload, finalize, assign, remove */
  action: string | null;
  /** Error classification */
  reason_kind: ReasonKind | null;
  /** UI surface that triggered the event */
  surface: string | null;
  /** Current route path (no query/hash) */
  route: string | null;
  /** RPC or edge function name involved */
  rpc_name: string | null;
  /** Machine-readable error identifier */
  error_code: string | null;
  /** HTTP status code from the backend response */
  http_status: number | null;
  /** Current status of the entity being mutated */
  entity_status: string | null;
  /** Bag for domain-specific fields that don't fit the canonical keys */
  extra: Record<string, unknown>;
}

export type SystemEventContextInput = Partial<SystemEventContext> & {
  /** Shorthand: any additional keys end up in extra */
  [key: string]: unknown;
};

const CANONICAL_KEYS = new Set<string>([
  'feature',
  'action',
  'reason_kind',
  'surface',
  'route',
  'rpc_name',
  'error_code',
  'http_status',
  'entity_status',
  'extra',
]);

/**
 * Normalizes a loose context object into the canonical shape.
 * Unknown keys are moved into `extra`. Never throws.
 */
export function buildSystemEventContext(
  input: SystemEventContextInput = {},
): SystemEventContext {
  const extra: Record<string, unknown> = { ...(input.extra as Record<string, unknown> ?? {}) };

  // Move non-canonical keys into extra
  for (const [key, value] of Object.entries(input)) {
    if (!CANONICAL_KEYS.has(key) && value !== undefined) {
      extra[key] = value;
    }
  }

  return {
    feature: asStringOrNull(input.feature),
    action: asStringOrNull(input.action),
    reason_kind: asStringOrNull(input.reason_kind) as ReasonKind | null,
    surface: asStringOrNull(input.surface),
    route: asStringOrNull(input.route) ?? safeRoute(),
    rpc_name: asStringOrNull(input.rpc_name),
    error_code: asStringOrNull(input.error_code),
    http_status: typeof input.http_status === 'number' ? input.http_status : null,
    entity_status: asStringOrNull(input.entity_status),
    extra,
  };
}

function asStringOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function safeRoute(): string | null {
  try {
    return typeof window !== 'undefined' ? window.location.pathname : null;
  } catch {
    return null;
  }
}
